import { getNineChapterParams, xposParamsMap, getStandardArguments } from '@didi/webx-js-core';
import { storage } from '../storage';
import { getURL, hashQuery, query } from '../query';
import { getOmega } from '../omega';
import { xenv } from '../xenv';
import { utils } from '../utils';
import { getBranch } from '../initSDK';
/**
 * getNineChapterCommonParams: 综合获取九章参数
 * @param url
 * @example
 * ```js
 *  const parameters = getNineChapterCommonParams()
 * ```
 */
const getNineChapterCommonParams = (useStorage = true) => {
    const collection = getNineChapterParams(getURL());
    if (useStorage && storage.webxNineChapterParams) { // 存在通过webx对外服务获取到的九章参数 则以该组参数为主
        const nineChapterParamsKey = [
            'dchn',
            'prod_key',
            'xbiz',
            'xpsid',
            'xoid',
            'xspm_from',
            'xpsid_from',
            'xpsid_root',
            'xpsid_share',
            'xpos_from'
        ];
        nineChapterParamsKey.forEach((key) => {
            if (storage.webxNineChapterParams[key])
                collection[key] = storage.webxNineChapterParams[key] || '';
        });
    }
    collection['xenv'] = xenv();
    if (!collection['xoid'] && getOmega() && getOmega().getXoid)
        collection['xoid'] = getOmega().getXoid();
    /* url参数&productInit请求中均未获取到用户信息的情况下 尝试通过缓存storage获取 */
    if (!collection['uid']) {
        if (storage.userInfo.uid) {
            // 首先尝试从storage缓存中获取之前请求用户信息的记录
            collection['uid'] = String(storage.userInfo.uid);
        }
    }
    return collection;
};
/**
 * getRuntimeStandardArgument: 获取运行时标准公参
 * @param url
 * @example
 * ```js
 *  const parameters = getRuntimeStandardArgument()
 * ```
 */
const getRuntimeStandardArgument = () => {
    const collection = getStandardArguments(getURL());
    console.log('collection ', getURL());
    collection['xenv'] = xenv(); // 获取运行时xenv
    collection['uid'] = storage.userInfo.uid ? Number(storage.userInfo.uid) : Number(collection['uid']); // 结合缓存获取用户信息
    collection['token'] = storage.userInfo.token ? String(storage.userInfo.token) : collection['token']; // 结合缓存获取用户信息
    collection['lat'] = storage.locationInfo.lat ? String(storage.locationInfo.lat) : collection['lat']; // 结合缓存获取经纬度
    collection['lng'] = storage.locationInfo.lng ? String(storage.locationInfo.lng) : collection['lng']; // 结合缓存获取经纬度
    collection['city_id'] = storage.locationInfo.cityId ? String(storage.locationInfo.cityId) : collection['city_id']; // 结合缓存获取经纬度
    collection['trip_city_id'] = storage.locationInfo.tripCityId ? storage.locationInfo.tripCityId : collection['trip_city_id']; // 结合缓存获取经纬度
    collection['passport_type'] = utils.getPassportType(getBranch()); // 获取运行时passport_type
    return collection;
};
const webxParamsKey = [
    /* 页面信息 */
    'xbiz',
    'prod_key',
    'xpsid',
    'dchn',
    'xoid',
    /* 用户信息 */
    /* 环境信息 */
    'xenv',
    /* 路径信息 */
    'xpsid_root',
    'xpsid_from',
    'xpsid_share',
    'xspm_from',
    'xpos_from',
    'xpos_share'
];
/**
 * getNineChapterWebxParams: 获取webx对外服务模块九章参数
 * @param
 * @example
 * ```js
 *  const parameters = getNineChapterWebxParams()
 * ```
 */
const getNineChapterWebxParams = (options = {
    useStorage: true,
    extParams: {
        xpos_share: false,
        xpsid_share: false
    }
}) => {
    const collection = getNineChapterCommonParams(options.useStorage);
    return webxParamsKey.reduce((result, key) => {
        switch (key) {
            case 'xpos_share':
                if (collection['xpos_share'] && options.extParams.xpos_share) {
                    result[key] = collection[key];
                }
                break;
            case 'xpsid_share':
                if (collection['xpsid_share'] && options?.extParams?.xpsid_share) {
                    result['xpsid_share'] = collection['xpsid_share'];
                }
                else {
                    result['xpsid_share'] = '';
                }
                break;
            default:
                if (collection[key])
                    result[key] = collection[key];
                break;
        }
        return result;
    }, {});
};
const gotoParamsKey = [
    /* 页面信息 */
    'xoid',
    /* 用户信息 */
    // 'openid',
    // 'token',
    // 'uid',
    /* 环境信息 */
    // 'xenv',
    // 'lat',
    // 'lng',
    // 'cityid',
    /* 路径信息 */
    'xpsid_root',
    'xpsid_from',
    // 'xpsid_share',
    'xspm_from',
    'xpos_from'
];
/**
 * getNineChapterGotoParams: 获取Goto模块九章参数
 * @param
 * @example
 * ```js
 *  const parameters = getNineChapterGotoParams()
 * ```
 */
const getNineChapterGotoParams = (option = {}) => {
    const collection = getNineChapterCommonParams();
    if (collection['xpsid_from'])
        collection['xpsid_from'] = '';
    if (collection['xpsid']) {
        collection['xpsid_from'] = collection['xpsid'];
        if (!collection['xpsid_root'])
            collection['xpsid_root'] = collection['xpsid'];
        collection['xpsid'] = undefined;
    }
    if (option.removeXoid)
        collection['xoid'] = undefined;
    return gotoParamsKey.reduce((result, key) => {
        if (collection[key])
            result[key] = collection[key];
        return result;
    }, {});
};
const sameProductGotoParamsKey = [
    /* 页面信息 */
    'xbiz',
    'prod_key',
    'xpsid',
    'dchn',
    'xoid',
    /* 用户信息 */
    'uid',
    /* 环境信息 */
    'xenv',
    /* 路径信息 */
    'xpsid_root',
    'xpsid_from',
    'xspm_from'
];
/**
 * getNineChapterSameProductGotoParams: 获取Goto模块九章参数
 * @param
 * @example
 * ```js
 *  const parameters = getNineChapterSameProductGotoParams()
 * ```
 */
const getNineChapterSameProductGotoParams = (option = {}) => {
    const collection = getNineChapterCommonParams();
    if (option.removeXoid)
        collection['xoid'] = undefined;
    return sameProductGotoParamsKey.reduce((result, key) => {
        if (collection[key])
            result[key] = collection[key];
        return result;
    }, {});
};
/**
 * parseXposParams: 解析处理xpose运营位参数
 * @param
 * @example
 * ```js
 *  const parameters = parseXposParams(shareCommonConfig['resourceInfo'])
 * ```
 */
const parseXposParams = (resourceInfo) => {
    if (resourceInfo) {
        /* 检查必填项 */
        const pass = ['resname', 'element_name', 'xspm'].reduce((list, item1) => {
            if (Object.keys(resourceInfo).find((item2) => item1 === item2))
                list.push(item1);
            return list;
        }, []);
        if (pass) {
            /* 默认绑定当前prod_key */
            if (!resourceInfo['prod_key']) {
                const collection = getNineChapterCommonParams();
                resourceInfo['prod_key'] = collection['prod_key'];
            }
            /* xpos参数映射 */
            const shineResourceInfo = Object.entries(resourceInfo).reduce((res, [key, value]) => {
                if (xposParamsMap[key]) { // 需要映射 进行key转换
                    res[xposParamsMap[key]] = value;
                }
                else {
                    res[key] = value;
                }
                return res;
            }, {});
            return JSON.stringify(shineResourceInfo);
        }
    }
    return '';
};
const parseXposParamsByXposData = (xposData) => {
    if (xposData) {
        /* 检查必填项 */
        const pass = ['cid', 'pk', 'en', 'idx'].reduce((list, item1) => {
            if (Object.keys(xposData).find((item2) => item1 === item2))
                list.push(item1);
            return list;
        }, []);
        if (pass.length) {
            /* 默认绑定当前prod_key */
            if (!xposData['pk']) {
                const collection = getNineChapterCommonParams();
                xposData['pk'] = collection['prod_key'];
            }
            /* xpos参数映射 */
            const shineResourceInfo = Object.entries(xposData).reduce((res, [key, value]) => {
                if (xposParamsMap[key]) { // 需要映射 进行key转换
                    res[xposParamsMap[key]] = value;
                }
                else {
                    res[key] = value;
                }
                return res;
            }, {});
            return JSON.stringify(shineResourceInfo);
        }
    }
    return '';
};
const shareParamsKey = [
    /* 路径信息 */
    'xpsid_share'
];
/**
 * getNineChapterShareParams: 获取分享模块九章参数
 * @param
 * @example
 * ```js
 *  const parameters = getNineChapterShareParams()
 * ```
 */
const getNineChapterShareParams = (shareCommonConfig = null) => {
    const collection = getNineChapterCommonParams();
    if (collection['xpsid']) {
        collection['xpsid_share'] = collection['xpsid'];
        collection['xpsid'] = undefined;
    }
    const initResult = {};
    /* 添加xpos */
    if (shareCommonConfig && shareCommonConfig.resourceInfo) {
        initResult['xpos_share'] = parseXposParams(shareCommonConfig.resourceInfo);
    }
    const result = shareParamsKey.reduce((result, key) => {
        if (collection[key])
            result[key] = collection[key];
        return result;
    }, initResult);
    return result;
};
const getNineChapterShareParamsNew = (shareCommonConfig = null) => {
    const collection = getNineChapterCommonParams();
    if (collection['xpsid']) {
        collection['xpsid_share'] = collection['xpsid'];
        collection['xpsid'] = undefined;
    }
    const initResult = {};
    /* 添加xpos */
    if (shareCommonConfig && shareCommonConfig.xposShare) {
        initResult['xpos_share'] = parseXposParamsByXposData(shareCommonConfig.xposShare);
    }
    const result = shareParamsKey.reduce((result, key) => {
        if (collection[key])
            result[key] = collection[key];
        return result;
    }, initResult);
    return result;
};
const hostList = [
    'page.udache.com',
    'prod.didi.cn',
    'pre-page.udache.com',
    'wyc-webx.intra.xiaojukeji.com',
    '10.190.7.204:8080',
    '10.190.26.137'
];
function addJumpH5CommonParams(loadParams, queryData, params) {
    if (!loadParams.url) {
        return {};
    }
    // 当前页面url参数
    const urlQuery = Object.assign({}, query(), hashQuery());
    // goto跳转的url上拼接参数以及goto params中参数
    const goToParams = Object.assign({}, queryData, params);
    let jumpParams = {};
    const hostReg = /^http(s)?:\/\/(.*?)\//;
    const host = hostReg?.exec?.(loadParams.url)?.[2] || '';
    const extParams = loadParams.extParams || {};
    const hostListCopy = utils.cloneDeep(hostList);
    if (extParams.domainList && Array.isArray(extParams.domainList)) {
        hostListCopy.push(...extParams.domainList);
    }
    // 短链 && （不限制域名 || 域名在白名单）|| 同产品 需要拼接
    const addParamsFlag = goToParams.prod_key && (extParams.unlimitedDomain || hostListCopy.indexOf(host) !== -1) || extParams.sameProduct;
    // 读取赤兔配置信息，并按需获取参数
    const needLocation = extParams.needLocation || 'none';
    const needLogin = extParams.needLogin || 'none';
    const hasNineParams = extParams.injectNineChapterParams === false;
    if (addParamsFlag || (hasNineParams && needLocation && needLocation !== 'none')) {
        urlQuery.openid && (jumpParams.openid = urlQuery.openid);
        urlQuery.appid && (jumpParams.appid = urlQuery.appid);
        urlQuery.lang && (jumpParams.lang = urlQuery.lang);
        storage.locationInfo?.lat && (jumpParams.lat = storage.locationInfo?.lat);
        storage.locationInfo?.lat && (jumpParams.latitude = storage.locationInfo?.lat);
        storage.locationInfo?.lng && (jumpParams.lng = storage.locationInfo?.lng);
        storage.locationInfo?.lng && (jumpParams.longitude = storage.locationInfo?.lng);
        storage.locationInfo?.cityId && (jumpParams.cityid = storage.locationInfo?.cityId);
    }
    if (addParamsFlag || (hasNineParams && needLogin && needLogin !== 'none')) {
        storage.userInfo?.token && (jumpParams.token = storage.userInfo?.token);
    }
    if (!urlQuery.openid) {
        gotoParamsTrackEvent('goto_params_openid_error');
    }
    if (!urlQuery.appid) {
        gotoParamsTrackEvent('goto_params_appid_error');
    }
    if (!urlQuery.lang) {
        gotoParamsTrackEvent('goto_params_lang_error');
    }
    if (goToParams.token && storage.userInfo?.token && goToParams.token !== storage.userInfo.token) {
        gotoParamsTrackEvent('goto_params_token_error');
    }
    if (goToParams.cityId && storage.locationInfo?.cityId && goToParams.cityId !== storage.locationInfo.cityId) {
        gotoParamsTrackEvent('goto_params_cityId_error');
    }
    return jumpParams;
}
function gotoParamsTrackEvent(type) {
    const urlQuery = Object.assign({}, query(), hashQuery());
    window?.Omega?.trackEvent?.('pub_webx_jssdk_error_bt', 'webx_jsssdk异常', {
        type,
        api: 'goTo',
        prod_key: urlQuery.prod_key,
        dchn: urlQuery.dchn,
        referer: window.document.referrer,
        xenv: xenv()
    });
}
export { getNineChapterCommonParams, getNineChapterWebxParams, getNineChapterGotoParams, getNineChapterSameProductGotoParams, getNineChapterShareParams, addJumpH5CommonParams, getNineChapterShareParamsNew, getRuntimeStandardArgument };
